import React, { useMemo, useCallback } from "react";
import { UniqueCitations } from "../types/models";
import { useDocumentStore } from "../stores/documentStore";
import { usePlatformStore } from "../stores/platformStore";
import { apiClient } from "../clients/APIClient";

interface CitationDialogProps {
  citation: UniqueCitations | null;
  onClose: () => void;
}

/** Check whether a file_url value from the backend is usable. */
const isValidFileUrl = (url: string | undefined | null): url is string =>
  typeof url === "string" && url.trim() !== "" && url !== "None";

const formatCitationFileName = (fileName: string, maxLength = 50): string => {
  if (fileName.length <= maxLength) return fileName;

  const lastDot = fileName.lastIndexOf(".");
  const hasExtension = lastDot > 0 && lastDot < fileName.length - 1;

  if (!hasExtension) {
    const tailLength = 20;
    const headLength = Math.max(12, maxLength - tailLength - 3);
    return `${fileName.slice(0, headLength)}...${fileName.slice(-tailLength)}`;
  }

  const extension = fileName.slice(lastDot);
  const baseName = fileName.slice(0, lastDot);
  const baseTailLength = Math.max(6, 20 - extension.length);
  const tail = `${baseName.slice(-baseTailLength)}${extension}`;
  const headLength = Math.max(12, maxLength - tail.length - 3);

  return `${baseName.slice(0, headLength)}...${tail}`;
};

/** Returns true if the value looks like a local filesystem path (not a URL). */
const isLocalPath = (value: string): boolean => {
  // Windows absolute path (C:\...) or UNC (\\server\share)
  if (/^[A-Za-z]:[\\/]/.test(value) || value.startsWith("\\\\")) return true;
  // Unix absolute path
  if (value.startsWith("/")) return true;
  return false;
};

export function CitationDialog({ citation, onClose }: CitationDialogProps) {
  const documents = useDocumentStore((state) => state.documents);
  const localFilePaths = useDocumentStore((state) => state.localFilePaths);
  const isDesktop = usePlatformStore((state) => state.isDesktop());

  const fileId = citation?.file_id ?? null;
  const document = useMemo(
    () => documents.find((candidate) => candidate.id === fileId),
    [documents, fileId],
  );
  const canViewOriginal = Boolean(document?.can_view_original);
  const citationUsesOriginal = Boolean(
    citation?.citations?.some(
      (item) => item.source?.metadata?.pii_phi_context === "original",
    ),
  );

  const resolvedPath = useMemo(() => {
    if (!fileId) return null;
    // 1. Path stored in citation source (persisted with chat history)
    const fromCitation = citation?.citations?.[0]?.source?.local_path;
    if (fromCitation && (isDesktop || !isLocalPath(fromCitation))) return fromCitation;
    // 2. Local path captured during upload (Electron only)
    const local = localFilePaths[fileId];
    if (local && (isDesktop || !isLocalPath(local))) return local;
    // 3. Backend-provided file_url
    return isValidFileUrl(document?.file_url) ? document.file_url : null;
  }, [fileId, citation, document, localFilePaths, isDesktop]);

  const handleOpenAtPage = useCallback(
    (page?: number, searchText?: string) => {
      // Electron — use IPC to open in a native BrowserWindow with findInPage.
      // Prefer the resolved local/remote path; fall back to the backend content
      if (window.electron?.openFileAtPage) {
        const targetPath = resolvedPath ?? (fileId ? apiClient.files.getFileContentUrl(fileId) : null);
        if (!targetPath) return;
        window.electron.openFileAtPage(targetPath, page, searchText);
        return;
      }
      // Web mode - open in new tab with URL fragment for page
      let fileUrl: string | null = null;
      if (resolvedPath && /^https?:\/\//i.test(resolvedPath)) {
        fileUrl = resolvedPath;
      } else if (fileId) {
        fileUrl = apiClient.files.getFileContentUrl(fileId);
      }
      if (!fileUrl) return;
      const fragments: string[] = [];
      if (page != null) fragments.push(`page=${page}`);
      const hash = fragments.length > 0 ? `#${fragments.join("&")}` : "";
      window.open(`${fileUrl}${hash}`, "_blank", "noopener,noreferrer");
    },
    [resolvedPath, fileId],
  );

  if (!citation) return null;

  const displayFileName = citation.file_name ? formatCitationFileName(citation.file_name) : null;
  // Show the button on Electron when a path is resolved, or on web when we have at least a file ID
  const hasFile = !!(resolvedPath || fileId);

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] rounded-2xl border border-gray-800 bg-zinc-950 text-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-5 py-4 flex items-center gap-3 flex-shrink-0">
          <i className="fas fa-list text-white text-lg flex-shrink-0" />
          <h2 className="text-md font-semibold text-white truncate" title={citation.file_name}>
            {displayFileName
              ? `${citationUsesOriginal ? "Protected" : "Masked"} citation: ${displayFileName}`
              : `${citationUsesOriginal ? "Protected" : "Masked"} citation`}
          </h2>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-lg border border-gray-700 bg-zinc-900 px-3 py-2 text-xs text-gray-300">
            {citationUsesOriginal
              ? "Your PII/PHI access allowed the model and this citation to use original document context. This access is audited."
              : "This citation is masked because the model received the masked document context."}
          </div>
          {citation.citations?.length ? (
            citation.citations.map((c, index) => (
              <div key={index} className="rounded-xl border border-gray-800 bg-black/40 p-4">
                <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-2 text-xs text-gray-300 mb-3 text-right">
                  <p>
                    <span className="font-semibold text-gray-200">Page:</span> {c.page ?? "N/A"}
                  </p>
                  {hasFile && (
                    <button
                      type="button"
                      onClick={() => handleOpenAtPage(c.page, c.retrieved_content)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-gray-200 bg-gray-800 border border-gray-700 hover:bg-red-700 hover:border-red-600 transition-colors"
                      title={canViewOriginal ? "Open protected original (audited)" : "Open masked document"}
                    >
                      <i className="fas fa-external-link-alt text-[10px]" aria-hidden="true" />
                      {canViewOriginal ? "Open Original File" : "Open Masked File"}
                    </button>
                  )}
                </div>

                <div className="rounded-lg border border-gray-700 bg-zinc-900/90 p-3 text-sm leading-relaxed text-gray-100 whitespace-pre-wrap">
                  {c.retrieved_content || "No content provided"}
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-gray-800 bg-black/40 p-6 text-center text-sm text-gray-400">
              No citation content is available for this file.
            </div>
          )}
        </div>

        <div className="px-5 py-3 bg-black/40 border-t border-gray-800 flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 hover:bg-red-700 hover:border-red-600 transition-colors flex items-center gap-2"
          >
            <i className="fas fa-times text-sm" aria-hidden="true" />
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
